import { useCallback } from 'react';

import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

import { createWorkflowToast } from '@/components/common/workflow-toast';
import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

export enum WorkflowExecutionStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  RECOVERING = 'RECOVERING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export type WorkflowStatusResponse = {
  correlationId: string;
  workflowName: string;
  status: WorkflowExecutionStatus;
  completionRate: number;
  startedAt: string;
  completedAt: string | null;
  entities: {
    logId: string;
    entityId: string;
    status: WorkflowExecutionStatus;
    completionRate: number | null;
    startedAt: string;
    completedAt: string | null;
    errorMessage: string | null;
    previousExecutionLogId: string | null;
  }[];
};

const POLL_INTERVAL_MS = 2000;
const REQUEST_TIMEOUT_MS = 15000;

const baseUrl = import.meta.env.VITE_API_BASE_URL;

export async function requestWorkflow<Data>(
  token: AccessToken,
  path: string,
  options: RequestInit,
): Promise<Data> {
  if (!token) {
    throw new Error(t('workflow.toast.noAuth'));
  }

  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const signal = options.signal
    ? AbortSignal.any([options.signal, timeout])
    : timeout;

  try {
    const response = await fetch(new URL(path, baseUrl), {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal,
    });

    if (!response.ok) {
      const body = await response.text();
      const message = t('workflow.toast.httpError', {
        status: response.status,
      });
      throw new Error(body ? `${message} ${body}` : message);
    }

    return await response.json();
  } catch (error) {
    if (timeout.aborted) {
      throw new Error(t('workflow.toast.requestTimeout'), { cause: error });
    }

    if (error instanceof TypeError) {
      throw new Error(t('workflow.toast.networkError'), { cause: error });
    }

    if (error instanceof SyntaxError) {
      throw new Error(t('workflow.toast.invalidResponse'), { cause: error });
    }

    throw error;
  }
}

function isFinished(status: WorkflowExecutionStatus | undefined) {
  return (
    status === WorkflowExecutionStatus.COMPLETED ||
    status === WorkflowExecutionStatus.FAILED ||
    status === WorkflowExecutionStatus.CANCELLED
  );
}

function workflowStatusQueryOptions(token: AccessToken, correlationId: string) {
  return queryOptions({
    queryKey: ['workflow-status', correlationId],
    enabled: !!token,
    async queryFn({ signal }) {
      const response = await requestWorkflow<WorkflowStatusResponse>(
        token,
        `/api/v2/workflow/${correlationId}`,
        { method: 'GET', signal },
      );

      if (
        response.correlationId !== correlationId ||
        !Array.isArray(response.entities)
      ) {
        throw new Error(t('workflow.toast.invalidResponse'));
      }

      return response;
    },
    refetchInterval: (query) =>
      isFinished(query.state.data?.status) ? false : POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: true,
    retryDelay: POLL_INTERVAL_MS,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useWorkflowStatus(correlationId: string) {
  const token = useAccessToken();
  return useQuery(workflowStatusQueryOptions(token, correlationId));
}

export type StartWorkflowResponse = { correlationId: string };

export type WorkflowDefinition = {
  name: string;
};

export type WorkflowParameters = { ids: string[] };

export type WorkflowCallOptions = {
  onFinished?: () => void;
};

type WorkflowVariables = WorkflowParameters & WorkflowCallOptions;

export function createWorkflow(workflow: WorkflowDefinition) {
  return function useWorkflow() {
    const { t } = useTranslation();
    const token = useAccessToken();
    const label = () =>
      t(`workflow.names.${workflow.name}`, {
        defaultValue: workflow.name,
      });

    const mutation = useMutation({
      async mutationFn({ ids }: WorkflowVariables) {
        const response = await requestWorkflow<StartWorkflowResponse>(
          token,
          '/api/v2/workflow',
          {
            method: 'POST',
            body: JSON.stringify({
              name: workflow.name,
              ids,
            }),
          },
        );

        if (!response.correlationId) {
          throw new Error(t('workflow.toast.invalidResponse'));
        }

        return response;
      },
      // A lost POST response may still have queued work, so never resubmit automatically.
      retry: false,
      onMutate(request) {
        return createWorkflowToast({
          title: t('workflow.toast.runningTitle', {
            workflow: label(),
            count: request.ids.length,
          }),
          errorTitle: t('workflow.toast.failedTitle', {
            workflow: label(),
          }),
        });
      },
      onSuccess({ correlationId }, request, notification) {
        notification?.track({
          correlationId,
          workflow,
          count: request.ids.length,
          onFinished: request.onFinished,
        });
      },
      onError(error, _request, notification) {
        notification?.error(
          error instanceof Error
            ? error.message
            : t('common.somethingWentWrongNotifyHelpDesk'),
          t('workflow.toast.submissionErrorTitle', { workflow: label() }),
        );
      },
    });
    const { mutateAsync } = mutation;
    const execute = useCallback(
      (parameters: WorkflowParameters, options?: WorkflowCallOptions) =>
        mutateAsync({ ...parameters, ...options }),
      [mutateAsync],
    );

    return [execute, mutation] as const;
  };
}
